import { withAuthenticationRequired } from "@auth0/auth0-react";
import {
  MenuItem,
  TextField,
  Grid,
  FormControl,
  Button,
  Paper,
  Box,
  Typography,
} from "@mui/material";
import { Sensors, Key, History, Cancel, Save, ElectricalServices } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { store } from "../store";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { postData } from "../API/Post";
import updateSnackbarMessage from "../functions/updateSnackbarMessage";
import { putData } from "../API/Put";
import fetchDevices from "../functions/fetchDevices";
import fetchData from "../API/Fetch";
import { useSelector } from "react-redux";
import DeviceHistoryTable from "../components/tables/DeviceHistory";
import logger from "../utils/logger";
import {
  fetchDeviceConfig,
  createDeviceConfig,
  updateDeviceConfig,
} from "../API/deviceConfig";

function DeviceUpdate() {
  const params = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const [device, setDevice] = useState(null);
  const [deviceConfig, setDeviceConfig] = useState(null);
  const devices = useSelector((state) => state.devices);
  const deviceBrands = useSelector((state) => state.deviceBrands);
  const deviceTypes = useSelector((state) => state.deviceTypes);

  const FLASHO_BRAND_ID = 71;

  // For debugging purposes only - remove in production
  useEffect(() => {
    logger.log("Device Brands:", deviceBrands);
    logger.log("Device Types:", deviceTypes);
  }, [deviceBrands, deviceTypes]);

  const { register, handleSubmit, control, reset, setValue, watch } = useForm({
    reValidateMode: "onBlur",
  });

  const watchBrandId = watch("device_brand_id");
  const watchMeterType = watch("meter_type");
  const isFlasho = Number(watchBrandId) === FLASHO_BRAND_ID;

  const validationLabels = {
    name: {
      required: t("common.required", { name: t("common.name") }),
    },
    device_brand_id: {
      required: t("common.required", { name: t("common.device_brand") }),
    },
    dev_eui: {
      required: t("common.required", { name: t("common.dev_eui") }),
      minLength: t("common.min_length", { length: 8 }),
      pattern: t("common.alphanumeric_only"),
    },
    app_eui: {
      minLength: t("common.min_length", { length: 16 }),
      maxLength: t("common.max_length", { length: 16 }),
      pattern: t("common.hexadecimal_only"),
    },
    app_key: {
      minLength: t("common.min_length", { length: 32 }),
      maxLength: t("common.max_length", { length: 32 }),
      pattern: t("common.hexadecimal_only"),
    },
  };

  const onSubmit = async (data) => {
    let { name, device_brand_id, dev_eui, app_eui, app_key } = data;

    let jsonData = {
      name: name,
      dev_eui: dev_eui,
      device_brand_id: device_brand_id,
      app_eui: app_eui,
      app_key: app_key,
    };

    if (device) {
      jsonData.dev_eui = device.dev_eui;
    }

    let response = null;

    if (device) {
      response = await putData(`/devices/${device.dev_eui}`, jsonData);
    } else {
      response = await postData(`/devices/`, jsonData);
    }

    if (response && response.status === 200) {
      // Sauvegarder la config Flash'O si applicable
      if (Number(data.device_brand_id) === FLASHO_BRAND_ID) {
        const configData = {
          dev_eui: device ? device.dev_eui : data.dev_eui,
          meter_type: data.meter_type || "linky",
          coeff_tc: parseFloat(data.coeff_tc) || 1,
          coeff_tt: parseFloat(data.coeff_tt) || 1,
          pulse_weight_wh: parseFloat(data.pulse_weight_wh) || 1,
          install_datetime: data.install_datetime || null,
          index_at_install: parseInt(data.index_at_install) || 0,
          notes: data.notes || "",
        };

        if (deviceConfig) {
          await updateDeviceConfig(configData.dev_eui, configData);
        } else {
          await createDeviceConfig(configData);
        }
      }

      updateSnackbarMessage(
        device === null
          ? t("common.added_success", { name: t("common.device") }) +
              " Redirecting to Devices..."
          : t("common.updated_success", { name: t("common.device") }) +
              " Redirecting to Devices..."
      );

      fetchDevices();
      navigate("/devices/");
      return;
    }
    if (response && response.status === 409) {
      updateSnackbarMessage(t("common.dev_eui_exists"));
      return;
    }
    updateSnackbarMessage(
      device === null
        ? t("common.add_error", { name: t("common.device") })
        : t("common.update_error", { name: t("common.device") })
    );
  };

  const generateNewName = (device_brand_id) => {
    // Find the brand object
    const brand = deviceBrands.find((b) => b.id === device_brand_id);
    let prefix =
      brand && brand.name
        ? brand.name.toLowerCase().replace(/\s+/g, "_") + "_"
        : "device_";
    let count =
      devices.filter(
        (device) =>
          device.device_brand && device.device_brand.id === device_brand_id
      ).length + 1;
    let countString = count.toString().padStart(3, "0");
    let name = prefix + countString;
    setValue("name", name);
    setValue("device_brand_id", device_brand_id);
  };

  useEffect(() => {
    store.dispatch({
      type: "pageActions/update",
      payload: [],
    });

    if (!params.id) {
      return;
    }

    store.dispatch({
      type: "pageTitle/update",
      payload: t("common.update") + " " + t("common.device"),
    });

    fetchData(`/devices/${params.id}`).then((data) => {
      setDevice(data);
    });
  }, [params.id, t]);

  useEffect(() => {
    if (!device) {
      return;
    }

    const { name, device_brand, dev_eui, app_eui, app_key } = device;

    reset({
      name: name,
      device_brand_id: device_brand ? device_brand.id : "",
      dev_eui: dev_eui,
      app_eui: app_eui,
      app_key: app_key,
    });

    // Charger la config Flash'O si c'est un Flash'O
    if (device_brand && device_brand.id === FLASHO_BRAND_ID) {
      fetchDeviceConfig(dev_eui).then((config) => {
        if (config) {
          setDeviceConfig(config);
          setValue("meter_type", config.meter_type || "linky");
          setValue("coeff_tc", config.coeff_tc || 1);
          setValue("coeff_tt", config.coeff_tt || 1);
          setValue("pulse_weight_wh", config.pulse_weight_wh || 1);
          setValue("install_datetime", config.install_datetime ? config.install_datetime.slice(0, 16) : "");
          setValue("index_at_install", config.index_at_install || 0);
          setValue("notes", config.notes || "");
        }
      });
    }
  }, [device, reset, setValue]);

  // Style commun pour les sections
  const sectionStyle = {
    p: 3,
    mb: 2,
    borderRadius: 2,
  };

  return (
    <Grid container spacing={2}>
      {/* Section Informations générales */}
      <Grid item xs={12}>
        <Paper sx={{ ...sectionStyle, borderLeft: "4px solid #78a882" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
            <Sensors sx={{ color: "#78a882" }} />
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              Informations générales
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Controller
                name="device_brand_id"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState: { error } }) => (
                  <FormControl fullWidth variant="outlined">
                    <TextField
                      id="device_brand_id"
                      label={t("common.device_brand")}
                      fullWidth
                      {...field}
                      error={error !== undefined}
                      helperText={error ? validationLabels.device_brand_id[error.type] : ""}
                      variant="outlined"
                      {...register("device_brand_id")}
                      select
                      value={field.value ? field.value : ""}
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) => generateNewName(Number(e.target.value))}
                    >
                      {deviceBrands && deviceBrands.length > 0 ? (
                        deviceBrands.map((brand) => (
                          <MenuItem key={brand.id} value={brand.id}>
                            {brand.name}
                            {brand.device_type && ` (${brand.device_type.name})`}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value="" disabled>
                          {t("common.loading")}
                        </MenuItem>
                      )}
                    </TextField>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="name"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState: { error } }) => (
                  <FormControl fullWidth variant="outlined">
                    <TextField
                      id="name"
                      label={t("common.name")}
                      fullWidth
                      {...field}
                      error={error !== undefined}
                      helperText={error ? validationLabels.name[error.type] : ""}
                      variant="outlined"
                      {...register("name")}
                      InputLabelProps={{ shrink: true }}
                    />
                  </FormControl>
                )}
              />
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* Section Identifiants LoRaWAN */}
      <Grid item xs={12}>
        <Paper sx={{ ...sectionStyle, borderLeft: "4px solid #6b9dad" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
            <Key sx={{ color: "#6b9dad" }} />
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              Identifiants LoRaWAN
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Controller
                name="dev_eui"
                disabled={device ? true : false}
                control={control}
                rules={{
                  required: true,
                  minLength: 8,
                  pattern: /^[a-zA-Z0-9]*$/,
                }}
                render={({ field, fieldState: { error } }) => (
                  <FormControl fullWidth variant="outlined">
                    <TextField
                      id="dev_eui"
                      label={t("common.dev_eui")}
                      fullWidth
                      {...field}
                      disabled={device ? true : false}
                      error={error !== undefined}
                      helperText={error ? validationLabels.dev_eui[error.type] : ""}
                      variant="outlined"
                      {...register("dev_eui")}
                      InputLabelProps={{ shrink: true }}
                    />
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="app_eui"
                control={control}
                rules={{
                  minLength: 16,
                  maxLength: 16,
                  pattern: /^[a-fA-F0-9]*$/,
                }}
                render={({ field, fieldState: { error } }) => (
                  <FormControl fullWidth variant="outlined">
                    <TextField
                      id="app_eui"
                      label={t("common.app_eui") + " (JoinEUI)"}
                      fullWidth
                      {...field}
                      error={error !== undefined}
                      helperText={error ? validationLabels.app_eui[error.type] : "16 caractères hexadécimaux (optionnel - pour ChirpStack)"}
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                      inputProps={{ maxLength: 16 }}
                      {...register("app_eui")}
                    />
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="app_key"
                control={control}
                rules={{
                  minLength: 32,
                  maxLength: 32,
                  pattern: /^[a-fA-F0-9]*$/,
                }}
                render={({ field, fieldState: { error } }) => (
                  <FormControl fullWidth variant="outlined">
                    <TextField
                      id="app_key"
                      label={t("common.app_key") + " (AppKey)"}
                      fullWidth
                      {...field}
                      error={error !== undefined}
                      helperText={error ? validationLabels.app_key[error.type] : "32 caractères hexadécimaux (optionnel - pour ChirpStack)"}
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                      inputProps={{ maxLength: 32 }}
                      {...register("app_key")}
                    />
                  </FormControl>
                )}
              />
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* Section Configuration compteur (Flash'O uniquement) */}
      {isFlasho && (
        <Grid item xs={12}>
          <Paper sx={{ ...sectionStyle, borderLeft: "4px solid #d4a843" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
              <ElectricalServices sx={{ color: "#d4a843" }} />
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                Configuration compteur
              </Typography>
            </Box>
            <Grid container spacing={2}>
              {/* Type de compteur */}
              <Grid item xs={12} md={4}>
                <Controller
                  name="meter_type"
                  control={control}
                  defaultValue="linky"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label="Type de compteur"
                      fullWidth
                      value={field.value || "linky"}
                      InputLabelProps={{ shrink: true }}
                    >
                      <MenuItem value="linky">Linky</MenuItem>
                      <MenuItem value="ace6000">ACE 6000</MenuItem>
                      <MenuItem value="actaris">Actaris</MenuItem>
                      <MenuItem value="trimaran2">Trimaran 2</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>

              {/* Coeff TC */}
              <Grid item xs={12} md={4}>
                <Controller
                  name="coeff_tc"
                  control={control}
                  defaultValue={1}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Coefficient TC"
                      type="number"
                      fullWidth
                      disabled={watchMeterType === "linky"}
                      helperText={
                        watchMeterType === "linky"
                          ? "Toujours 1 pour Linky"
                          : watchMeterType === "ace6000"
                          ? "Registre 006 (ex: 20, 40, 200, 400)"
                          : watchMeterType === "actaris"
                          ? "Ex: 10, 20, 40, 100"
                          : "Registre 041 (ex: 20, 40)"
                      }
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: "any", min: 0 }}
                    />
                  )}
                />
              </Grid>

              {/* Coeff TT (visible seulement pour Trimaran 2) */}
              <Grid item xs={12} md={4}>
                <Controller
                  name="coeff_tt"
                  control={control}
                  defaultValue={1}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Coefficient TT"
                      type="number"
                      fullWidth
                      disabled={watchMeterType !== "trimaran2"}
                      helperText={
                        watchMeterType === "trimaran2"
                          ? "Registre 042"
                          : "Uniquement pour Trimaran 2"
                      }
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: "any", min: 0 }}
                    />
                  )}
                />
              </Grid>

              {/* Poids impulsion */}
              <Grid item xs={12} md={4}>
                <Controller
                  name="pulse_weight_wh"
                  control={control}
                  defaultValue={1}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Poids impulsion (Wh)"
                      type="number"
                      fullWidth
                      helperText="Valeur par défaut : 1 Wh"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: "any", min: 0 }}
                    />
                  )}
                />
              </Grid>

              {/* Date d'installation */}
              <Grid item xs={12} md={4}>
                <Controller
                  name="install_datetime"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Date d'installation"
                      type="datetime-local"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>

              {/* Index à la pose */}
              <Grid item xs={12} md={4}>
                <Controller
                  name="index_at_install"
                  control={control}
                  defaultValue={0}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Index à la pose (Wh brut)"
                      type="number"
                      fullWidth
                      helperText="Index lu sur le compteur au moment de la pose"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: 0 }}
                    />
                  )}
                />
              </Grid>

              {/* Notes */}
              <Grid item xs={12}>
                <Controller
                  name="notes"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Notes"
                      fullWidth
                      multiline
                      rows={2}
                      helperText="Ex: câblage bornes 3+4 modifié, registre 006 = 40"
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      )}

      {/* Boutons d'action */}
      <Grid item xs={12}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Cancel />}
            onClick={() => navigate("/devices/")}
            sx={{
              borderColor: "rgba(255,255,255,0.3)",
              color: "rgba(255,255,255,0.7)",
              "&:hover": {
                borderColor: "rgba(255,255,255,0.5)",
                backgroundColor: "rgba(255,255,255,0.05)",
              },
            }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSubmit(onSubmit)}
            sx={{
              backgroundColor: "#78a882",
              "&:hover": { backgroundColor: "#6a9a74" },
            }}
          >
            {device === null ? t("common.add") : t("common.update")}
          </Button>
        </Box>
      </Grid>

      {/* Section Historique */}
      {params.id && (
        <Grid item xs={12}>
          <Paper sx={{ ...sectionStyle, borderLeft: "4px solid #c4a574" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
              <History sx={{ color: "#c4a574" }} />
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                Historique du device
              </Typography>
            </Box>
            <DeviceHistoryTable dev_eui={params.id} />
          </Paper>
        </Grid>
      )}
    </Grid>
  );
}

export default withAuthenticationRequired(DeviceUpdate);
